/**
 * localStorage 再送キュー (§4.4)。Firestore SDK の書込はタブ破棄で消滅し得る
 * (sendBeacon を使わない) ため、visibilitychange(hidden) 時とネットワーク断
 * リトライ中に dirty ペイロードを同期書込しておく。
 *
 * キー形式: `grandmap.resend.<email>|<docPath>` (| は email に出現しない区切り)
 *
 * - 再送専用。**読取ソースには決して使わない** (§4.4)
 * - 回収時はサーバ現値の updatedBy と照合し、一致 (自分が最終書込者)→復元 /
 *   不一致 (他者が後から更新)→破棄 (盲目的再送はしない)。判定・実装は
 *   呼び出し側 (ResendRecoveryDialog.vue) の責務。baseUpdatedAt はこの照合には
 *   使わない (下記フィールド doc 参照)。
 */
export interface ResendEntry {
  docPath: string;
  payload: Record<string, unknown>;
  /**
   * flush 基準の updatedAt (ISO)。新規 doc 等で無ければ null。
   * デバッグ用に保持しているのみで、回収時の復元可否判定には使わない
   * (判定は updatedBy 照合方式 — ResendRecoveryDialog.vue の restore() 参照)。
   */
  baseUpdatedAt: string | null;
  /** enqueue 時刻 (ISO)。 */
  at: string;
}

export interface ResendQueue {
  enqueue(
    docPath: string,
    payload: Record<string, unknown>,
    baseUpdatedAt: string | null,
  ): void;
  ack(docPath: string): void;
  entries(): ResendEntry[];
}

export function createResendQueue(
  email: string,
  storage: Storage = localStorage,
): ResendQueue {
  const prefix = `grandmap.resend.${email}|`;

  function enqueue(
    docPath: string,
    payload: Record<string, unknown>,
    baseUpdatedAt: string | null,
  ): void {
    try {
      const entry: ResendEntry = {
        docPath,
        payload,
        baseUpdatedAt,
        at: new Date().toISOString(),
      };
      storage.setItem(prefix + docPath, JSON.stringify(entry));
    } catch {
      // 容量超過等 — キューは補償機構なので本体の保存は妨げない
    }
  }

  function ack(docPath: string): void {
    try {
      storage.removeItem(prefix + docPath);
    } catch {
      /* noop */
    }
  }

  function entries(): ResendEntry[] {
    const out: ResendEntry[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      try {
        out.push(JSON.parse(storage.getItem(key) ?? '') as ResendEntry);
      } catch {
        // 壊れたエントリは黙って読み飛ばす (次の ack/enqueue で上書きされる)
      }
    }
    return out;
  }

  return { enqueue, ack, entries };
}
